import React, { ComponentProps, forwardRef, HTMLInputTypeAttribute } from 'react';
import { TextField, Label, InputProps } from 'react-aria-components';
import { FieldError, UseFormRegisterReturn } from 'react-hook-form';

type InputTextProps = InputProps & {
  name: string;
  type: HTMLInputTypeAttribute | 'textarea';
  label?: string;
  placeholder?: string;
  supportText?: string;
  required?: boolean;
  disabled?: boolean;
  register?: UseFormRegisterReturn;
  errors?: FieldError | undefined;
  rows?: number;
};

type ChildProps = (ComponentProps<'input'> | ComponentProps<'textarea'>) & InputTextProps;

const InputText = forwardRef<HTMLInputElement | HTMLTextAreaElement, ChildProps>(
  (
    {
      name,
      type,
      label,
      placeholder,
      supportText,
      required,
      disabled,
      register,
      errors,
      rows,
      ...rest
    },
    ref
  ) => {
    return (
      <TextField
        aria-label={`${name}-InputText`}
        name={name}
        isRequired={required}
        isDisabled={disabled}
        className="flex w-full flex-col"
      >
        {label && (
          <div className="flex flex-row items-center">
            <Label className="mb-2 text-lg font-bold">{label}</Label>
            {required && <span className="mb-2 ml-1 text-red-800">※必須</span>}
          </div>
        )}
        {supportText && <span className="mb-2 text-sm text-gray-500">{supportText}</span>}
        {type === 'textarea' ? (
          <textarea
            name={name}
            disabled={disabled}
            rows={rows || 4}
            className="mb-2 block w-full resize-y rounded-md border border-[#1A1A1A] px-3 py-2 focus:border-2 focus:border-yellow-700 disabled:border-solid-gray-420 disabled:bg-solid-gray-50 disabled:text-solid-gray-420 sm:text-sm"
            placeholder={placeholder}
            ref={ref as React.ForwardedRef<HTMLTextAreaElement>}
            {...register}
            {...(rest as ComponentProps<'textarea'>)}
          />
        ) : (
          <input
            name={name}
            type={type as HTMLInputTypeAttribute}
            disabled={disabled}
            className="mb-2 block h-14 rounded-md border border-[#1A1A1A] px-3 py-2 focus:border-2 focus:border-yellow-700 disabled:border-solid-gray-420 disabled:bg-solid-gray-50 disabled:text-solid-gray-420 sm:text-sm"
            placeholder={placeholder}
            ref={ref as React.ForwardedRef<HTMLInputElement>}
            {...register}
            {...(rest as ComponentProps<'input'>)}
          />
        )}
        <div className="mb-2 w-fit text-sm text-red-800">{errors?.message}</div>
      </TextField>
    );
  }
);

InputText.displayName = 'InputText';

export default InputText;
